<template>
  <div class='mvp-view'>
    <template v-if='renderedHtml'>
      <iframe
        ref='frame'
        :srcdoc='renderedHtml'
        sandbox='allow-scripts allow-popups allow-forms allow-modals'
        class='mvp-frame'
      />
      <mvp-feedback-bar v-if='tracking' :mvp-id='mvp._id' class='mt-2' />
    </template>
    <div v-else class='mvp-state'>
      <template v-if='loading'>
        <b-spinner variant='primary' class='mb-3' />
        <span class='mvp-state-text'>Carregando o MVP...</span>
      </template>

      <template v-else>
        <b-icon icon='exclamation-circle' font-scale='3' class='mvp-state-icon mb-3' />
        <strong class='mvp-state-title'>MVP não encontrado</strong>
        <span class='mvp-state-text'>
          Este conteúdo não está mais disponível — pode ter sido removido ou o endereço está incorreto.
        </span>
        <base-button class='mt-4' @click="$router.push('/web')">Ir para Seleção de agentes</base-button>
      </template>
    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import MvpFeedbackBar from '@/components/Mvp/MvpFeedbackBar.vue'
import { injectTracker, TRACKER_SOURCE } from '@/components/Mvp/mvpTracker'

const FLUSH_INTERVAL_MS = 10000
const HEARTBEAT_INTERVAL_MS = 30000
const MAX_BUFFER = 200
const EVENTS_PER_REQUEST = 100
const PENDING_KEY = 'mvpPendingEvents'
const MAX_PENDING_BATCHES = 20

export default {
  name: 'MvpView',
  components: { MvpFeedbackBar },
  data() {
    return {
      mvp: null,
      renderedHtml: '',
      loading: true,
      tracking: false,
      visitId: '',
      visibleSeconds: 0,
      lastVisibleAt: 0,
      buffer: [],
      sending: [],
      flushTimer: null,
      heartbeatTimer: null
    }
  },
  computed: {
    isAdmin() {
      const user = this.$store.getters['user/current']
      return !!user && user.role === 'admin'
    },
    currentUserId() {
      const user = this.$store.getters['user/current']
      return user && user.id ? String(user.id) : ''
    }
  },
  methods: {
    ...mapActions('mvp', {
      handleGetMvpBySlug: 'handleGetMvpBySlug',
      handleGetMvp: 'handleGetMvp',
      handleSendMvpEvents: 'handleSendMvpEvents'
    }),
    startTracking() {
      this.visitId = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`
      this.lastVisibleAt = Date.now()
      this.resendPending()
      this.pushEvent({ type: 'view' })
      this.flush()

      this.flushTimer = setInterval(this.flush, FLUSH_INTERVAL_MS)
      this.heartbeatTimer = setInterval(this.heartbeat, HEARTBEAT_INTERVAL_MS)
      window.addEventListener('message', this.onMessage)
      window.addEventListener('pagehide', this.onLeave)
      document.addEventListener('visibilitychange', this.onVisibilityChange)
    },
    stopTracking() {
      clearInterval(this.flushTimer)
      clearInterval(this.heartbeatTimer)
      window.removeEventListener('message', this.onMessage)
      window.removeEventListener('pagehide', this.onLeave)
      document.removeEventListener('visibilitychange', this.onVisibilityChange)
      this.onLeave()
    },
    secondsOnPage() {
      const visibleNow = document.visibilityState === 'visible'
        ? (Date.now() - this.lastVisibleAt) / 1000
        : 0
      return Math.round(this.visibleSeconds + visibleNow)
    },
    pushEvent(event) {
      this.buffer.push({ ...event, visitId: this.visitId, clientAt: new Date().toISOString() })
      if (this.buffer.length > MAX_BUFFER) {
        this.buffer.splice(0, this.buffer.length - MAX_BUFFER)
      }
    },
    heartbeat() {
      if (document.visibilityState === 'visible') {
        this.pushEvent({ type: 'heartbeat', secondsOnPage: this.secondsOnPage() })
      }
    },
    onVisibilityChange() {
      if (document.visibilityState === 'hidden') {
        this.visibleSeconds += (Date.now() - this.lastVisibleAt) / 1000
        this.pushEvent({ type: 'heartbeat', secondsOnPage: this.secondsOnPage() })
        this.flushKeepalive()
      } else {
        this.lastVisibleAt = Date.now()
      }
    },
    onMessage(event) {
      const frame = this.$refs.frame
      const data = event.data
      if (!frame || event.source !== frame.contentWindow || !data || data.source !== TRACKER_SOURCE) {
        return
      }
      if (data.type === 'click') {
        this.pushEvent({
          type: 'click',
          name: String(data.name || '').slice(0, 200),
          tag: String(data.tag || '').slice(0, 50)
        })
      }
    },
    takeBatches() {
      const events = this.buffer.splice(0)
      const batches = []
      for (let i = 0; i < events.length; i += EVENTS_PER_REQUEST) {
        batches.push(events.slice(i, i + EVENTS_PER_REQUEST))
      }
      return batches
    },
    readPending() {
      try {
        const stored = JSON.parse(localStorage.getItem(PENDING_KEY))
        return Array.isArray(stored) ? stored : []
      } catch (e) {
        return []
      }
    },
    writePending(batches) {
      try {
        localStorage.setItem(PENDING_KEY, JSON.stringify(batches.slice(-MAX_PENDING_BATCHES)))
      } catch (e) {
        return
      }
    },
    persistBatch(events) {
      const batch = {
        key: `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`,
        mvpId: this.mvp._id,
        userId: this.currentUserId,
        events
      }
      this.writePending([...this.readPending(), batch])
      return batch
    },
    clearPending(key) {
      this.writePending(this.readPending().filter((batch) => batch.key !== key))
    },
    sendBatch(batch) {
      this.sending.push(batch.key)
      this.handleSendMvpEvents({ id: batch.mvpId, events: batch.events })
        .then(() => this.clearPending(batch.key))
        .catch(() => {})
        .finally(() => {
          this.sending = this.sending.filter((key) => key !== batch.key)
        })
    },
    resendPending() {
      this.readPending().forEach((batch) => {
        if (batch.userId === this.currentUserId && !this.sending.includes(batch.key)) {
          this.sendBatch(batch)
        }
      })
    },
    flush() {
      this.takeBatches().forEach((events) => {
        this.sendBatch(this.persistBatch(events))
      })
      this.resendPending()
    },
    flushKeepalive() {
      const sessionId = localStorage.getItem('sessionId')
      const tokenHofacil = localStorage.getItem('tokenHofacil')
      this.takeBatches().forEach((events) => {
        const batch = this.persistBatch(events)
        try {
          fetch(`${process.env.VUE_APP_BASE_URL}/mvps/${batch.mvpId}/events`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              sessionId,
              Authorization: tokenHofacil
            },
            body: JSON.stringify({ events }),
            credentials: 'include',
            keepalive: true
          })
            .then((response) => {
              if (response.ok) {
                this.clearPending(batch.key)
              }
            })
            .catch(() => {})
        } catch (e) {
          return
        }
      })
    },
    onLeave() {
      if (document.visibilityState === 'visible') {
        this.pushEvent({ type: 'heartbeat', secondsOnPage: this.secondsOnPage() })
      }
      this.flushKeepalive()
    }
  },
  async mounted() {
    try {
      const { preview } = this.$route.query
      const isPreview = Boolean(preview) && this.isAdmin
      const mvp = isPreview
        ? await this.handleGetMvp(preview)
        : await this.handleGetMvpBySlug(this.$route.params.slug)

      if (mvp && mvp.htmlContent) {
        this.mvp = mvp
        this.tracking = !isPreview
        this.renderedHtml = this.tracking ? injectTracker(mvp.htmlContent) : mvp.htmlContent
      }
    } catch (e) {
      this.renderedHtml = ''
    } finally {
      this.loading = false
    }

    if (this.tracking) {
      this.startTracking()
    }
  },
  beforeDestroy() {
    if (this.tracking) {
      this.stopTracking()
    }
  }
}
</script>

<style lang='scss' scoped>
.mvp-view {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: calc(100vh - 140px);
  min-height: 500px;
}

.mvp-frame {
  flex: 1;
  width: 100%;
  min-height: 0;
  border: 1px solid #e2e6ee;
  border-radius: 8px;
  background: #ffffff;
}

.mvp-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  width: 100%;
  height: 100%;
  padding: 0 16px;
  background: #eef1f6;
  border: 1px solid #e2e6ee;
  border-radius: 8px;
}

.mvp-state-icon {
  color: #04a7f3;
}

.mvp-state-title {
  font-size: 20px;
  font-weight: 700;
  color: #022245;
  margin-bottom: 6px;
}

.mvp-state-text {
  font-size: 15px;
  color: #808080;
  max-width: 420px;
}
</style>
